import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Key } from "lucide-react";
import { Button } from "@/components/ui";
import { MessageBox } from "@/components/ui/MessageBox";
import { useGenerateLicense, useRegenerateLicense } from "@/hooks/useLicenses";
import { useToast } from "@/hooks/useToast";
import { getLicenseAnchorsError, normalizeLicenseAnchors } from "@/utils/licenseValidation";
export function LicenseGenerateForm({ contractId, mode = "generate", submitLabel = "Generate", pendingLabel = "Generating...", successMessage = "License generated successfully", onCancel, onSuccess }) {
    const [licenseAnchorsInput, setLicenseAnchorsInput] = useState("");
    const [didAttemptSubmit, setDidAttemptSubmit] = useState(false);
    const generateMutation = useGenerateLicense();
    const regenerateMutation = useRegenerateLicense();
    const toast = useToast();
    const mutation = mode === "regenerate" ? regenerateMutation : generateMutation;
    const licenseAnchorsError = getLicenseAnchorsError(licenseAnchorsInput);
    const licenseAnchors = normalizeLicenseAnchors(licenseAnchorsInput);
    const showLicenseAnchorsError = !!licenseAnchorsError && (didAttemptSubmit || licenseAnchorsInput.trim().length > 0);
    async function handleGenerate(event) {
        event.preventDefault();
        setDidAttemptSubmit(true);
        if (licenseAnchorsError) return;
        try {
            await mutation.mutateAsync({
                contractId,
                licenseAnchors
            });
            toast.success(successMessage);
            setLicenseAnchorsInput("");
            setDidAttemptSubmit(false);
            onSuccess?.();
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to generate license";
            toast.error(message);
        }
    }
    return /*#__PURE__*/ _jsxs("form", {
        onSubmit: handleGenerate,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        htmlFor: `license-anchors-${contractId}`,
                        className: "block text-sm font-semibold text-gray-800",
                        children: "Appliance license anchors"
                    }),
                    /*#__PURE__*/ _jsx("textarea", {
                        id: `license-anchors-${contractId}`,
                        value: licenseAnchorsInput,
                        onChange: (e)=>{
                            setLicenseAnchorsInput(e.target.value);
                        },
                        placeholder: "Enter one 64-character anchor per line",
                        rows: 6,
                        className: "mt-2 w-full resize-y rounded-lg border border-navy-20 bg-white px-3 py-2.5 font-mono text-xs text-navy shadow-sm outline-none placeholder:text-navy-50 focus:border-primary focus:ring-2 focus:ring-primary/20",
                        spellCheck: false,
                        autoCapitalize: "none",
                        autoCorrect: "off",
                        autoFocus: true
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-1.5 text-xs text-gray-500",
                        children: "Use one line per appliance. Each anchor must contain exactly 64 characters."
                    }),
                    showLicenseAnchorsError && /*#__PURE__*/ _jsx(MessageBox, {
                        className: "mt-3 px-4 py-2",
                        message: licenseAnchorsError,
                        type: "error"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-6 flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end",
                children: [
                    onCancel && /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        variant: "simple",
                        color: "navy-light",
                        onClick: onCancel,
                        disabled: mutation.isPending,
                        className: "!inline-flex !items-center !justify-center",
                        children: "Cancel"
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        type: "submit",
                        variant: "simple",
                        color: "navy-90",
                        disabled: licenseAnchors.length === 0 || !!licenseAnchorsError || mutation.isPending,
                        className: "!inline-flex !items-center !justify-center",
                        children: [
                            /*#__PURE__*/ _jsx(Key, {
                                className: "mr-2 h-4 w-4"
                            }),
                            mutation.isPending ? pendingLabel : submitLabel
                        ]
                    })
                ]
            })
        ]
    });
}
